/* ============================================================================
   assets/bpp-toast.js — page-level toast messages.

   WHAT THIS IS FOR — AND WHAT IT DELIBERATELY IS NOT FOR
   -----------------------------------------------------
   This theme already announces most of what a toast would normally announce.
   Before adding a toast to a flow, check this list; a second announcement of
   the same fact is worse than no toast at all:

     add to cart (success) .... snippets/cart-drawer.liquid #CartDrawer-LiveRegionText
                                sections/main-cart-items.liquid #cart-live-region-text
                                snippets/cart-notification.liquid (role="dialog", takes focus)
                                sections/bpp-sticky-cart.liquid (aria-live on the host)
     add to cart (failure) .... snippets/buy-buttons.liquid .product-form__error-message-wrapper
                                (role="alert")
     line item quantity ....... #shopping-cart-line-item-status / #CartDrawer-LineItemStatus
     price / availability ..... role="status" on the price block in bpp-main-product,
                                main-product, featured-product; .bpp-pdp__summary-line
     result counts ............ main-search, main-collection-product-grid, facets
     pizza builder ............ sections/bpp-pizza-builder.liquid [data-bpp-preview-note]
                                (role="status") and [data-bpp-error] (role="alert")

   So the toast is the surface for confirmations that have NOWHERE ELSE to go —
   promo code applied, address saved, account preference updated, "link copied",
   and anything a future BPP component does that leaves no status node on the
   page. It is never the only signal: the control that triggered the action
   still updates itself.

   Because of that overlap, ANNOUNCING IS OPT-IN. By default a toast is a
   visual confirmation only and adds nothing to the accessibility timeline.
   Pass `announce: true` only when you have checked that nothing in the list
   above already speaks for your action.

   PUBLIC API
   ----------
   House style is assets/pubsub.js, so publishing is the primary API. The event
   name is not in PUB_SUB_EVENTS because that file is Shopify's; it is exported
   here instead, and on window.BPPToast.EVENT.

     publish(BPP_TOAST_EVENT, {
       message:  'Promo code applied',   // required, plain text, never HTML
       tone:     'success',              // 'success' | 'error' | 'info' (default)
       announce: true,                   // default false — see above
       duration: 6000                    // ms; 0 = stays until dismissed
     });

   For code that runs before pubsub.js, or outside a theme script:

     window.BPPToast.show({ message: 'Address saved', tone: 'success' });
     window.BPPToast.show('Address saved');   // shorthand, tone 'info'
     window.BPPToast.dismissAll();

   Calls made before the element upgrades are queued and flushed on connect.

   ANNOUNCEMENT MECHANICS
   ----------------------
   snippets/bpp-toast.liquid ships an EMPTY <p aria-live="polite" aria-atomic>
   in the page. A live region has to be in the DOM, and quiet, before text is
   written into it — a region inserted together with its text is not reliably
   announced. This file only ever writes text into that node; it never creates
   it.

   The visible .bpp-toast carries NO role="status" and NO aria-live. role=status
   is itself a polite atomic live region, so a role="status" node appended into
   an aria-live container is a region nested in a region — some screen reader /
   browser pairs announce it twice, and the nested one is created together with
   its own text, which is the case that fails. One region, one writer.
   ========================================================================== */

const BPP_TOAST_EVENT = 'bpp:toast';
const BPP_TOAST_TONES = ['success', 'error', 'info'];
/* More than three stacked toasts is a wall, not a message. */
const BPP_TOAST_MAX = 3;

class BppToasts extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
  }

  connectedCallback() {
    if (this.ready) return;

    this.stack = this.querySelector('[data-toast-stack]');
    this.announcer = this.querySelector('[data-toast-announcer]');
    this.shell = this.querySelector('[data-toast-template]');
    if (!this.stack || !this.announcer || !this.shell) return;

    this.ready = true;
    this.timers = new WeakMap();
    this.returnFocus = null;
    this.announcedBy = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    if (typeof subscribe === 'function') {
      this.unsubscribe = subscribe(BPP_TOAST_EVENT, (data) => this.show(data));
    }

    BppToastQueue.attach(this);
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.ready = false;
  }

  /* ---- raising ---------------------------------------------------------- */

  show(options) {
    if (!this.ready) return null;

    const settings = typeof options === 'string' ? { message: options } : options || {};
    // Text only. A product title can contain & or <, and the toast is the one
    // place a caller is tempted to hand over markup. Everything goes through
    // textContent below, so it cannot.
    const message = String(settings.message == null ? '' : settings.message).trim();
    if (!message) return null;

    const tone = BPP_TOAST_TONES.includes(settings.tone) ? settings.tone : 'info';
    const toast = this.build(message, tone);
    if (!toast) return null;

    this.stack.appendChild(toast);
    this.trim();

    if (settings.announce === true) this.announce(toast, message);
    this.startTimer(toast, this.resolveDuration(settings.duration, tone, message));

    return toast;
  }

  build(message, tone) {
    const source = this.shell.content.firstElementChild;
    if (!source) return null;

    const toast = source.cloneNode(true);
    toast.classList.add(`bpp-toast--${tone}`);
    toast.dataset.tone = tone;

    const text = toast.querySelector('.bpp-toast__text');
    if (text) text.textContent = message;

    const slot = toast.querySelector('.bpp-toast__icon');
    const icon = this.querySelector(`[data-toast-icon="${tone}"]`);
    if (slot && icon) slot.appendChild(icon.content.cloneNode(true));

    // Pointer listeners go on the toast itself: mouseenter/pointerenter do not
    // bubble, so they cannot be delegated from the container.
    toast.addEventListener('pointerenter', () => this.pause(toast));
    toast.addEventListener('pointerleave', () => this.resume(toast));

    return toast;
  }

  announce(toast, message) {
    // Clear first, then write on the next tick. Two toasts raised in the same
    // task would otherwise overwrite the region before the assistive tech has
    // read it, and it would speak only the second.
    this.announcer.textContent = '';
    this.announcedBy = toast;
    window.setTimeout(() => {
      if (this.announcedBy === toast && toast.isConnected) this.announcer.textContent = message;
    }, 100);
  }

  /* ---- dismissal --------------------------------------------------------
     Auto-dismiss steals a message from someone still reading it; never
     dismissing piles them up. The compromise:
       - success / info auto-dismiss on a clock scaled to the length of the
         message, floor 4s, ceiling 10s;
       - errors never auto-dismiss — they usually name something the shopper
         has to do about it;
       - the clock pauses on hover, while focus is inside the toast, and while
         the tab is in the background, and resumes when that stops being true;
       - every toast has a 44px close button and answers Escape;
       - at most three at once, oldest dropped first.
     ---------------------------------------------------------------------- */

  resolveDuration(value, tone, message) {
    if (value === 0 || value === Infinity) return 0;
    if (typeof value === 'number' && isFinite(value) && value > 0) return value;
    if (tone === 'error') return 0;
    const words = message.split(/\s+/).length;
    return Math.min(10000, Math.max(4000, 2000 + words * 400));
  }

  startTimer(toast, duration) {
    if (!duration) return;
    this.timers.set(toast, { remaining: duration, start: 0, id: 0 });
    this.resume(toast);
  }

  pause(toast) {
    const timer = this.timers.get(toast);
    if (!timer || !timer.id) return;
    window.clearTimeout(timer.id);
    timer.id = 0;
    // Leave at least a beat on the clock so a pointer brushing past a toast
    // does not make it vanish the instant the pointer leaves.
    timer.remaining = Math.max(1000, timer.remaining - (Date.now() - timer.start));
  }

  resume(toast) {
    const timer = this.timers.get(toast);
    if (!timer || timer.id) return;
    if (document.hidden) return;
    if (toast.contains(document.activeElement)) return;
    if (typeof toast.matches === 'function' && toast.matches(':hover')) return;
    timer.start = Date.now();
    timer.id = window.setTimeout(() => this.dismiss(toast), timer.remaining);
  }

  clearTimer(toast) {
    const timer = this.timers.get(toast);
    if (timer && timer.id) window.clearTimeout(timer.id);
    this.timers.delete(toast);
  }

  dismiss(toast) {
    if (!toast || toast.dataset.leaving === 'true') return;
    toast.dataset.leaving = 'true';
    this.clearTimer(toast);

    if (this.announcedBy === toast) {
      this.announcer.textContent = '';
      this.announcedBy = null;
    }

    const hadFocus = toast.contains(document.activeElement);
    const finish = () => {
      toast.remove();
      if (hadFocus) this.restoreFocus();
    };

    if (this.reducedMotion.matches) {
      finish();
      return;
    }

    toast.classList.add('bpp-toast--leaving');
    let done = false;
    const once = () => {
      if (done) return;
      done = true;
      finish();
    };
    toast.addEventListener('transitionend', once, { once: true });
    // transitionend never fires if the toast is display:none'd or the
    // transition is overridden; the fallback guarantees removal either way.
    window.setTimeout(once, 400);
  }

  dismissAll() {
    Array.from(this.stack.children).forEach((toast) => this.dismiss(toast));
  }

  trim() {
    const live = Array.from(this.stack.children).filter((toast) => toast.dataset.leaving !== 'true');
    live.slice(0, Math.max(0, live.length - BPP_TOAST_MAX)).forEach((toast) => this.dismiss(toast));
  }

  restoreFocus() {
    const next = this.stack.querySelector('.bpp-toast:not([data-leaving]) [data-toast-close]');
    if (next) {
      next.focus();
      return;
    }
    // A dismissed toast must not drop focus onto <body>. Go back to whatever
    // the shopper was on before they tabbed into the stack.
    if (this.returnFocus && this.returnFocus.isConnected) this.returnFocus.focus();
    this.returnFocus = null;
  }

  /* ---- events ----------------------------------------------------------- */

  onClick(event) {
    const button = event.target.closest('[data-toast-close]');
    if (button) this.dismiss(button.closest('.bpp-toast'));
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;
    const toast = event.target.closest('.bpp-toast');
    if (!toast) return;
    event.stopPropagation();
    this.dismiss(toast);
  }

  onFocusIn(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) this.returnFocus = event.relatedTarget;
    const toast = event.target.closest('.bpp-toast');
    if (toast) this.pause(toast);
  }

  onFocusOut(event) {
    const toast = event.target.closest('.bpp-toast');
    if (toast && !toast.contains(event.relatedTarget)) this.resume(toast);
  }

  onVisibilityChange() {
    Array.from(this.stack.children).forEach((toast) => {
      if (document.hidden) this.pause(toast);
      else this.resume(toast);
    });
  }
}

/* Holds calls made before the element upgrades — a deferred script that runs
   ahead of this one still gets its toast. */
const BppToastQueue = {
  instance: null,
  pending: [],

  attach(element) {
    this.instance = element;
    const queued = this.pending;
    this.pending = [];
    queued.forEach((options) => element.show(options));
  },

  push(options) {
    if (this.instance && this.instance.isConnected) return this.instance.show(options);
    this.pending.push(options);
    return null;
  },
};

window.BPPToast = {
  EVENT: BPP_TOAST_EVENT,
  show(options) {
    return BppToastQueue.push(typeof options === 'string' ? { message: options } : options);
  },
  dismissAll() {
    if (BppToastQueue.instance) BppToastQueue.instance.dismissAll();
  },
};

if (!customElements.get('bpp-toasts')) {
  customElements.define('bpp-toasts', BppToasts);
}
